import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quickstart-for-adobe-pdf-services-api-nodejs"
    }}>{`Quickstart for Adobe PDF Services API (Node.js)`}</h1>
    <p>{`To get started using Adobe PDF Services API, let's walk through a simple scenario - taking an input PDF document and exporting it to Microsoft Word. In this guide, we will walk you through the complete process for creating a program that will accomplish this task. `}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To complete this guide, you will need:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://nodejs.org"
        }}>{`Node.js`}</a>{` - Node.js version 14.0 or higher is required. `}</li>
      <li parentName="ul">{`An Adobe ID. If you do not have one, the credential setup will walk you through creating one.`}</li>
      <li parentName="ul">{`A way to edit code. No specific editor is required for this guide.`}</li>
    </ul>
    <h2 {...{
      "id": "step-one-getting-credentials"
    }}>{`Step One: Getting credentials`}</h2>
    <p>{`1) To begin, open your browser to `}<a parentName="p" {...{
        "href": "https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api"
      }}>{`https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api`}</a>{`. If you are not already logged in to Adobe.com, you will need to sign in or create a new user. Using a personal email account is recommend and not a federated ID.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "750px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/5530d/shot1.webp 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0c8fb/shot1.webp 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/3ea8a/shot1.webp 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/dd4a7/shot1.png 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0f09e/shot1.png 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png",
            "alt": "Sign in",
            "title": "Sign in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) After registering or logging in, you will then be asked to name your new credentials. Use the name, "New Project". `}</p>
    <p>{`3) Change the "Choose language" setting to "Node.js". `}</p>
    <p>{`4) Also note the checkbox by, "Create personalized code sample." This will include a large set of samples along with your credentials. These can be helpful for learning more later. `}</p>
    <p>{`5) Click the checkbox saying you agree to the developer terms and then click "Create credentials."`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "70%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/5530d/shot2_spc.webp 320w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/0c8fb/shot2_spc.webp 640w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/94b1e/shot2_spc.webp 1280w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/09047/shot2_spc.webp 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/dd4a7/shot2_spc.png 320w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/0f09e/shot2_spc.png 640w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/bbbf7/shot2_spc.png 1280w", "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/bd320/shot2_spc.png 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/c15958e3ad6cfe74637c5f9d97626ad1/bbbf7/shot2_spc.png",
            "alt": "Project setup",
            "title": "Project setup",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`6) After your credentials are created, they are automatically downloaded:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/5530d/shot3_spc.webp 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0c8fb/shot3_spc.webp 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/94b1e/shot3_spc.webp 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/c5b4b/shot3_spc.webp 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/dd4a7/shot3_spc.png 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0f09e/shot3_spc.png 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/d866c/shot3_spc.png 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-two-setting-up-the-project"
    }}>{`Step Two: Setting up the project`}</h2>
    <p>{`1) In your Downloads folder, find the ZIP file with your credentials: PDFServicesSDK-Node.jsSamples.zip. If you unzip that archive, you will find a folder of samples and the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "399px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.37499999999999%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/07ff36411bc9cb0e6a8db11cae1d3224/5530d/shot5_spc.webp 320w", "/document-services/docs/static/07ff36411bc9cb0e6a8db11cae1d3224/9792e/shot5_spc.webp 399w"],
            "sizes": "(max-width: 399px) 100vw, 399px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/07ff36411bc9cb0e6a8db11cae1d3224/dd4a7/shot5_spc.png 320w", "/document-services/docs/static/07ff36411bc9cb0e6a8db11cae1d3224/f7599/shot5_spc.png 399w"],
            "sizes": "(max-width: 399px) 100vw, 399px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/07ff36411bc9cb0e6a8db11cae1d3224/f7599/shot5_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Take the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` and place it in a new directory. Remember that these credential files are important and should be stored safely.`}</p>
    <p>{`3) At the command line, change to the directory you created, and initialize a new Node.js project with `}<inlineCode parentName="p">{`npm init -y`}</inlineCode></p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "700px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "31.5625%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/5530d/shot7.webp 320w", "/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/0c8fb/shot7.webp 640w", "/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/84c6d/shot7.webp 700w"],
            "sizes": "(max-width: 700px) 100vw, 700px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/dd4a7/shot7.png 320w", "/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/0f09e/shot7.png 640w", "/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/a27c6/shot7.png 700w"],
            "sizes": "(max-width: 700px) 100vw, 700px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/b74ea3688c47c1502a9a31b757fab1aa/a27c6/shot7.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`4) Install the Adobe PDF Services Node.js SDK by typing `}<inlineCode parentName="p">{`npm install --save @adobe/pdfservices-node-sdk`}</inlineCode>{` at the command line.`}</p>
    <p>{`5) Install a package to help us work with ZIP files. Type `}<inlineCode parentName="p">{`npm install --save adm-zip`}</inlineCode>{`.`}</p>
    <p>{`At this point, we've installed the Node.js SDK for Adobe PDF Services API as a dependency for our project and have copied over our credentials files. `}</p>
    <p>{`Our application will take a PDF, `}<inlineCode parentName="p">{`Bodea Brochure.pdf`}</inlineCode>{` (downloadable from `}<a href="https://documentcloud.adobe.com/view-sdk-demo/PDFs/Bodea Brochure.pdf">{`here`}</a>{`) and convert it to a Microsoft Word document, `}<inlineCode parentName="p">{`Bodea Brochure.docx`}</inlineCode>{`.`}</p>
    <p>{`6) In your editor, open the directory where you previously copied the credentials. Create a new file, `}<inlineCode parentName="p">{`export.js`}</inlineCode>{`.`}</p>
    <p>{`Now you're ready to begin coding.`}</p>
    <h2 {...{
      "id": "step-three-creating-the-application"
    }}>{`Step Three: Creating the application`}</h2>
    <p>{`1) We'll begin by including our required dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const PDFServicesSdk = require('@adobe/pdfservices-node-sdk');
const fs = require('fs');
`}</code></pre>
    <p>{`The first line includes the Adobe PDF Services Node.js SDK. The second third include Node's `}<inlineCode parentName="p">{`filesystem`}</inlineCode>{` package. `}</p>
    <p>{`2) Now let's define our input and output:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const OUTPUT = './Bodea Brochure.docx';

// If our output already exists, remove it so we can run the application again.
if(fs.existsSync(OUTPUT)) fs.unlinkSync(OUTPUT);

const INPUT = './Bodea Brochure.pdf';
`}</code></pre>
    <p>{`3) Set the environment variables `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_SECRET`}</inlineCode>{` by running the following commands and replacing placeholders `}<inlineCode parentName="p">{`YOUR CLIENT ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`YOUR CLIENT SECRET`}</inlineCode>{` with the credentials present in `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Windows:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`MacOS/Linux:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p>{`4) Next, we setup the SDK to use our credentials.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const credentials =  PDFServicesSdk.Credentials
        .servicePrincipalCredentialsBuilder()
        .withClientId(process.env.PDF_SERVICES_CLIENT_ID)
        .withClientSecret(process.env.PDF_SERVICES_CLIENT_SECRET)
        .build();

// Create an ExecutionContext using credentials
const executionContext = PDFServicesSdk.ExecutionContext.create(credentials);
`}</code></pre>
    <p>{`This code both points to the credentials downloaded previously as well as sets up an execution context object that will be used later.`}</p>
    <p>{`5) Now, let's create the operation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// This creates an instance of the Export operation we're using, as well as specifying output type (DOCX)
const exportPdfOperation = PDFServicesSdk.ExportPDF.Operation.createNew(PDFServicesSdk.ExportPDF.SupportedTargetFormats.DOCX);

// Set operation input from a source file
const inputPDF = PDFServicesSdk.FileRef.createFromLocalFile(INPUT);
exportPdfOperation.setInput(inputPDF);
`}</code></pre>
    <p>{`This set of code defines what we're doing (an Export operation), points to our local file and specifies the input is a PDF, and then defines options for the Export call. In this example, the only option is the export format, DOCX.`}</p>
    <p>{`6) The next code block executes the operation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`try {

    exportPdfOperation.execute(executionContext)
    .then(result => result.saveAsFile(OUTPUT))
    .then(() => {
        console.log('Export Done')
    })
    .catch(err => {
        console.log('Exception encountered while executing operation', err);
    });

} catch(err) {
    console.error('Error:', err);
}
`}</code></pre>
    <p>{`This code runs the Export process and then stores the result Word document to the file system. `}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1098px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "19.375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/5530d/shot9.webp 320w", "/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/0c8fb/shot9.webp 640w", "/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/7ec41/shot9.webp 1098w"],
            "sizes": "(max-width: 1098px) 100vw, 1098px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/dd4a7/shot9.png 320w", "/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/0f09e/shot9.png 640w", "/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/23e59/shot9.png 1098w"],
            "sizes": "(max-width: 1098px) 100vw, 1098px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/1f2f78f78937d3ea1a39ef064e76e906/23e59/shot9.png",
            "alt": "Example running at the command line",
            "title": "Example running at the command line",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Here's the complete application (`}<inlineCode parentName="p">{`export.js`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const PDFServicesSdk = require('@adobe/pdfservices-node-sdk');
const fs = require('fs');

const OUTPUT = './Bodea Brochure.docx';

// If our output already exists, remove it so we can run the application again.
if(fs.existsSync(OUTPUT)) fs.unlinkSync(OUTPUT);

const INPUT = './Bodea Brochure.pdf';


console.log(\`About to export \${INPUT} to \${OUTPUT}.\`);

// Set up our credentials object.
const credentials =  PDFServicesSdk.Credentials
        .servicePrincipalCredentialsBuilder()
        .withClientId(process.env.PDF_SERVICES_CLIENT_ID)
        .withClientSecret(process.env.PDF_SERVICES_CLIENT_SECRET)
        .build();

// An exectuionContext object wraps our credentials
const executionContext = PDFServicesSdk.ExecutionContext.create(credentials);

// This creates an instance of the Export operation we're using, as well as specifying output type (DOCX)
const exportPdfOperation = PDFServicesSdk.ExportPDF.Operation.createNew(PDFServicesSdk.ExportPDF.SupportedTargetFormats.DOCX);

// Set operation input from a source file
const inputPDF = PDFServicesSdk.FileRef.createFromLocalFile(INPUT);
exportPdfOperation.setInput(inputPDF);

try {

    exportPdfOperation.execute(executionContext)
    .then(result => result.saveAsFile(OUTPUT))
    .then(() => {
        console.log('Export Done')
    })
    .catch(err => {
        console.log('Exception encountered while executing operation', err);
    });

} catch(err) {
    console.error('Error:', err);
}
`}</code></pre>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`Now that you've successfully performed your first operation, `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/document-services/docs/overview/pdf-services-api/"
      }}>{`review the documentation`}</a>{` for many other examples and reach out on our `}<a parentName="p" {...{
        "href": "https://community.adobe.com/t5/document-services-apis/ct-p/ct-Document-Cloud-SDK"
      }}>{`forums`}</a>{` with any questions. Also remember the samples you downloaded while creating your credentials also have many demos.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      